import { useEffect, useState } from 'react';
import { Configuration, RetailersApi } from '@/api';
import { EmptyResult } from '@/models/Pagination';
import { Retailer, Retailers } from '@/models/Retailer';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { setPaginationResultIfNotLoading } from '@/utils/pagination';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const initialState = EmptyResult<Retailer>();

interface UseFindRetailersQueryResult {
  retailers: Retailers;
  isLoading: boolean;
  error: Error | null;
  itemsPerPage: number;
  invalidateQuery: () => Promise<void>;
}

const useFindRetailersQuery = (
  page: number,
  searchQuery?: string,
  supplierId?: string,
): UseFindRetailersQueryResult => {
  const queryClient = useQueryClient();
  const [retailers, setRetailers] = useState<Retailers>(initialState);

  const retailersApi = new RetailersApi(new Configuration(useAuth));

  const itemsPerPage = 10;

  const { isLoading, data, isFetching, error } = useQuery({
    queryKey: supplierId
      ? ['retailers', supplierId, page, searchQuery]
      : ['retailers', page, searchQuery],
    queryFn: async () => {
      if (supplierId) {
        return retailersApi.findRetailersBySupplier(
          supplierId,
          searchQuery,
          page * itemsPerPage - itemsPerPage,
          itemsPerPage,
        );
      }
      return retailersApi.findRetailers(
        searchQuery,
        page * itemsPerPage - itemsPerPage,
        itemsPerPage,
      );
    },
  });

  useEffect(() => {
    setPaginationResultIfNotLoading(data, setRetailers, isLoading);
  }, [data, isLoading, isFetching]);

  const invalidateQuery = async () => {
    return queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === 'retailers',
    });
  };

  return {
    retailers,
    isLoading,
    error,
    itemsPerPage,
    invalidateQuery,
  };
};

export default useFindRetailersQuery;
